import React, { useEffect, useState } from 'react';
import styles from './Break.module.css';
import { getTherapistBreaks } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import Echo from '../../../../Components/Sockect/Echo';
import { getOutletExtendedFlagForDate } from '../../../../Utils/utils';

const Break = ({ therapistId, startDate }) => {
    const { selectedOutlet, selectedOutletData } = useGlobalContext();
    const [breaksData, setBreaksData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (therapistId) {
            fetchBreaksData();
        }
    }, [therapistId, startDate, selectedOutlet]);

    const formatDateDDMMYYYY = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDateRange = (baseDate) => {
        if (!baseDate) return '';
        
        const startDay = new Date(baseDate);
        const endDay = new Date(baseDate);
        endDay.setDate(startDay.getDate() + 6); // Add 6 days for 7-day range
        
        return `${formatDateDDMMYYYY(startDay)} / ${formatDateDDMMYYYY(endDay)}`;
    };

    const fetchBreaksData = async () => {
        setLoading(true);
        try {
            const dateRange = formatDateRange(startDate);
            const selectedDateStr = startDate ? formatDateDDMMYYYY(new Date(startDate)) : undefined;
            const calendarDate = startDate ? new Date(startDate) : new Date();
            const isCalendarExpansion = getOutletExtendedFlagForDate(selectedOutletData?.timing, calendarDate) ? 1 : 0;
            
            const response = await getTherapistBreaks({ 
                therapist: therapistId,
                outlet: selectedOutlet,
                pagination: 0,
                daterange: dateRange,
                selected_date: selectedDateStr,
                is_calendar_expansion: isCalendarExpansion,
            });
            
            if (response?.data?.success && response?.data?.data?.list?.leave) {
                const formattedData = formatBreaksData(response.data.data.list.leave);
                setBreaksData(formattedData);
            } else {
                setBreaksData([]);
            }
        } catch (error) {
            console.error('Error fetching breaks data:', error);
            setBreaksData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleBreaksUpdated = () => {
            if (therapistId) {
                fetchBreaksData();
            }
        };

        window.addEventListener('breaksUpdated', handleBreaksUpdated);
        return () => window.removeEventListener('breaksUpdated', handleBreaksUpdated);
    }, [therapistId, startDate, selectedOutlet]);

    useEffect(() => {
        if (!Echo || typeof Echo.channel !== "function") return;

        const channel = Echo.channel("breaks");
        const handleBreakEvent = (event) => {
            const eventStatus = event?.status;
            if (eventStatus === 'created' || eventStatus === 'updated' || eventStatus === 'deleted') {
                if (therapistId) {
                    fetchBreaksData();
                }
            }
        };

        channel.listen(".BreakChanged", handleBreakEvent);

        return () => {
            if (typeof channel.stopListening === "function") {
                channel.stopListening(".BreakChanged");
            }
        };
    }, [therapistId, startDate, selectedOutlet]);

    const formatBreaksData = (leaveData) => {
        // Group breaks by date
        const groupedByDay = {};
        
        leaveData.forEach(item => {
            const date = item.date; // "Thu, Oct 2025"
            
            if (!groupedByDay[date]) {
                groupedByDay[date] = [];
            }
            
            // Calculate duration from start_at and end_at
            const duration = calculateDuration(item.start_at, item.end_at);
            // Format time from start_at and end_at
            const time = formatTimeRange(item.start_at, item.end_at);
            
            groupedByDay[date].push({
                id: item.id,
                therapistName: item.user_name,
                statusName: item.status_name,
                date: item.date,
                duration: duration,
                time: time, // Format from start_at and end_at
                reason: item.remark || item.status_name || '-',
                createdAt: item.created_at
            });
        });

        // Generate all 7 days starting from startDate
        const allDays = generateWeekDays(startDate);
        
        // Map breaks to each day
        return allDays.map(dayInfo => ({
            date: dayInfo.formattedDate,
            dayName: dayInfo.dayName,
            dateOnly: dayInfo.dateOnly,
            breaks: groupedByDay[dayInfo.formattedDate] || []
        }));
    };

    const formatTimeRange = (startAt, endAt) => {
        if (!startAt || !endAt) return '-';
        
        try {
            // Parse time from format "2025-10-16 13:00:PM" and convert to 24-hour format "HH:MM"
            const formatTime = (timeStr) => {
                const [datePart, timePart] = timeStr.split(' ');
                const [time, period] = [timePart.slice(0, -2), timePart.slice(-2)];
                const [hours, minutes] = time.split(':').map(Number);
                
                // Convert to 24-hour format
                let hour24 = hours;
                
                // If hours >= 13, it's already in 24-hour format, use as-is
                if (hours >= 13) {
                    hour24 = hours;
                } else if (period === 'PM' && hours !== 12) {
                    hour24 = hours + 12;
                } else if (period === 'AM' && hours === 12) {
                    hour24 = 0;
                }
                
                // Return time in 24-hour format HH:MM
                return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            };
            
            const startTime = formatTime(startAt);
            const endTime = formatTime(endAt);
            
            return `${startTime} - ${endTime}`;
        } catch (error) {
            console.error('Error formatting time range:', error);
            return '-';
        }
    };

    const calculateDuration = (startAt, endAt) => {
        if (!startAt || !endAt) return '-';
        
        try {
            // Parse time from format "2025-10-16 09:50:AM" or "2025-10-16 13:00:PM"
            const parseTime = (timeStr) => {
                const [datePart, timePart] = timeStr.split(' ');
                const [time, period] = [timePart.slice(0, -2), timePart.slice(-2)];
                const [hours, minutes] = time.split(':').map(Number);
                
                let hour24 = hours;
                
                // If hours >= 13, it's already in 24-hour format, use as-is
                if (hours >= 13) {
                    hour24 = hours;
                } else if (period === 'PM' && hours !== 12) {
                    hour24 = hours + 12;
                } else if (period === 'AM' && hours === 12) {
                    hour24 = 0;
                }
                
                return hour24 * 60 + minutes; // Return total minutes
            };
            
            const startMinutes = parseTime(startAt);
            const endMinutes = parseTime(endAt);
            const durationMinutes = endMinutes - startMinutes;
            
            return `${durationMinutes} min`;
        } catch (error) {
            console.error('Error calculating duration:', error);
            return '-';
        }
    };

    const generateWeekDays = (baseDate) => {
        const days = [];
        const startDay = baseDate ? new Date(baseDate) : new Date();
        
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        for (let i = 0; i < 7; i++) {
            const currentDay = new Date(startDay);
            currentDay.setDate(startDay.getDate() + i);
            
            const dayName = dayNames[currentDay.getDay()];
            const date = currentDay.getDate();
            const month = monthNames[currentDay.getMonth()];
            const year = currentDay.getFullYear();
            
            // Match API format: "Fri, May 8" (no day zero-padding)
            const formattedDate = `${dayName.substring(0, 3)}, ${month} ${date}`;
            
            days.push({
                formattedDate: formattedDate,
                dayName: dayName,
                dateOnly: `${date} ${month}`
            });
        }
        
        return days;
    };

    if (loading) {
        return (
            <div className={styles.breakContainer}>
                <div className={styles.loadingContent}>
                  <Loader />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.breakContainer}>
            <div className={styles.tableWrapper}>
                <table className={styles.breaksTable}>
                    <thead>
                        <tr>
                            <th>Day</th>
                            <th>Duration</th>
                            <th>Time</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {breaksData.map((dayData, dayIndex) => {
                            // If day has breaks, show them
                            if (dayData.breaks.length > 0) {
                                return dayData.breaks.map((breakItem, breakIndex) => (
                                    <tr key={`${dayIndex}-${breakIndex}`}>
                                        {breakIndex === 0 ? (
                                            <td rowSpan={dayData.breaks.length} className={styles.dayCell}>
                                                <div className={styles.dayInfo}>
                                                    <div className={styles.dayName}>{dayData.dayName}</div>
                                                    <div className={styles.dayDate}>{dayData.dateOnly}</div>
                                                </div>
                                            </td>
                                        ) : null}
                                        <td>{breakItem.duration}</td>
                                        <td>{breakItem.time}</td>
                                        <td>{breakItem.reason}</td>
                                    </tr>
                                ));
                            } else {
                                // Show dashes for days without breaks
                                return (
                                    <tr key={dayIndex}>
                                        <td className={styles.dayCell}>
                                            <div className={styles.dayInfo}>
                                                <div className={styles.dayName}>{dayData.dayName}</div>
                                                <div className={styles.dayDate}>{dayData.dateOnly}</div>
                                            </div>
                                        </td>
                                        <td className={styles.dashCell}>-</td>
                                        <td className={styles.dashCell}>-</td>
                                        <td className={styles.dashCell}>-</td>
                                    </tr>
                                );
                            }
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Break;

